<!--
    Created by 程雨喵'mac on 2024/9/2.
    Copyright © 2024年 云柜-金星晖. All rights reserved.
    功能概述：计费设置-按电量计费（新）
    电费固定单价 + 服务费按 4 种维度互斥单选（时间/功率阶梯/使用电量/次数）+ 保底服务费
-->
<style lang="less">
.device-price-use-electric-new {
  .type-tit {
    padding-bottom: @containerGap;
    font-weight: 600;
    color: @textColorLight;
  }
  // 段前留白
  // （注意：项目里的 padding-primary-top 未定义，此处本地补齐）
  .type-tit-sep {
    padding-top: @containerGap;
  }
  // 表单行：标签列宽 120px 与弹窗 FormItem 的 label-width 对齐
  .form-row {
    display: flex;
    align-items: flex-start;
    padding-bottom: 12px;
    font-size: 12px;
    .form-row-label {
      width: 120px;
      flex: 0 0 120px;
      padding-right: 8px;
      text-align: right;
      line-height: @formHeight;
    }
    .form-row-value {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      min-height: @formHeight;
      line-height: @formHeight;
      .ivu-input-number {
        width: 90px;
        flex: 0 0 90px;
        margin-right: 8px;
        padding-right: 0;
        .ivu-input-number-handler-wrap {
          display: none;
        }
      }
      .row-unit {
        color: @textColorLight;
      }
    }
  }
  // 说明框上间距
  // （注意：项目里的 margin-primary-top 未定义，此处本地补齐）
  .alert-gap {
    margin-top: @containerGap;
  }
  // 说明文案尺寸与表单保持一致（与 useElectric 相同处理）
  .ivu-alert-with-desc {
    padding: @containerGap * 0.5 @containerGap;
    .ivu-alert-message {
      font-size: 13px;
    }
    .ivu-alert-desc {
      font-size: 12px;
      line-height: 18px;
    }
  }
  .money-rules-table {
    border: 1px solid @lineColorNormal;
    border-bottom: 0;
    border-radius: @borderRadiusMin;
    .money-rules-table-row {
      display: flex;
      align-items: flex-start;
      border-bottom: 1px solid @lineColorNormal;
      line-height: @formHeight;
      font-size: 12px;
      &.is-header {
        background-color: @backColorNormal;
        color: @textColorLight;
        font-weight: 600;
        font-size: 13px;
      }
      .table-col {
        padding: 5px;
        .display-flex();
        .table-col-inner {
          width: 100%;
          .display-flex();
          white-space: nowrap;
          .ivu-input-number {
            flex: 1;
          }
        }
        &+.table-col {
          border-left: 1px solid @lineColorNormal;
        }
        &.col-t-0 {
          .flex-grow(100px);
          font-weight: 600;
          color: @textColorLight;
        }
        &.col-t-1 {
          flex: 1;
        }
        &.col-t-2 {
          .flex-grow(180px);
        }
        &.col-t-3 {
          .flex-grow(80px);
        }
      }
    }
  }
  .add-btn-row {
    position: relative;
    .add-btn {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      .display-flex();
      border: 1px dashed @themeColor;
      background-color: rgba(255,255,255,0.7);
      color: @themeColor;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
    }
  }
  @keyframes errorAmin {
    0% { background-color: transparent }
    50% { background-color: rgba(254,85,113,0.2) }
    100% { background-color: transparent }
  }
  .row-is-error {
    animation: errorAmin 0.5s;
    animation-iteration-count: 3;
  }
}
</style>

<template>
  <div class="device-price-use-electric-new">
    <!-- ==================== 电费单价 ==================== -->
    <div class="type-tit">
      <span v-if="isEdit">设置</span>电费单价
    </div>

    <!-- 电费单价（固定） -->
    <div class="form-row">
      <div class="form-row-label">
        电费单价
      </div>
      <div class="form-row-value">
        <template v-if="isEdit">
          <InputNumber
            v-model="model.chargePrice"
            :min="0"
          />
          <span class="row-unit">元/度（固定单价，不区分时段）</span>
        </template>
        <span v-else>{{ model.chargePrice }} 元/度（固定单价，不区分时段）</span>
      </div>
    </div>

    <!-- ==================== 保底服务费 ==================== -->
    <div class="type-tit type-tit-sep">
      <span v-if="isEdit">设置</span>保底服务费
    </div>
    <div class="form-row">
      <div class="form-row-label">
        保底服务费
      </div>
      <div class="form-row-value">
        <template v-if="isEdit">
          <InputNumber
            v-model="model.minServiceFee"
            :min="0"
          />
          <span class="row-unit">元（低于该金额时按该金额收取）</span>
        </template>
        <span v-else>{{ h5MinServiceFee }}</span>
      </div>
    </div>

    <!-- ==================== 服务费 ==================== -->
    <div class="type-tit type-tit-sep">
      <span v-if="isEdit">设置</span>服务费
    </div>

    <!-- 服务费收取方式 -->
    <div class="form-row">
      <div class="form-row-label">
        服务费收取方式
      </div>
      <div class="form-row-value">
        <RadioGroup
          v-if="isEdit"
          v-model="model.serviceFeeType"
          @on-change="onFeeTypeChange"
        >
          <Radio
            v-for="(op, p) in feeTypeOptions"
            :key="p + '-op'"
            :label="op.value"
          >
            {{ op.label }}
          </Radio>
        </RadioGroup>
        <span v-else>{{ feeTypeLabel }}</span>
      </div>
    </div>

    <!-- 功率取值规则（仅按功率阶梯时展示） -->
    <div
      v-if="model.serviceFeeType === 2"
      class="form-row"
    >
      <div class="form-row-label">
        功率取值规则
      </div>
      <div class="form-row-value">
        <RadioGroup
          v-if="isEdit"
          v-model="model.powerValueType"
        >
          <Radio
            v-for="(op, p) in powerValueTypeOptions"
            :key="p + '-pv'"
            :label="op.value"
          >
            {{ op.label }}
          </Radio>
        </RadioGroup>
        <span v-else>{{ powerValueTypeLabel }}</span>
      </div>
    </div>

    <!-- 服务费单价（非功率阶梯：行内编辑） -->
    <div
      v-if="model.serviceFeeType && model.serviceFeeType !== 2 && !!dataList.length"
      class="form-row"
    >
      <div class="form-row-label">
        服务费单价
      </div>
      <div class="form-row-value">
        <template v-if="isEdit">
          <InputNumber
            v-model="dataList[0].servicePrice"
            :min="0"
          />
          <span class="row-unit">{{ unitText }}</span>
        </template>
        <span v-else>{{ dataList[0] && dataList[0].servicePrice }} {{ unitText }}</span>
      </div>
    </div>

    <!-- 功率阶梯（参照按功率计费的档位表格） -->
    <div
      v-else-if="model.serviceFeeType === 2 && !!dataList.length"
      class="money-rules-table"
    >
      <!--头部-->
      <div class="money-rules-table-row is-header">
        <div
          v-for="(item, i) in tableHeaders"
          :key="i"
          class="table-col"
          :class="'col-t-' + i"
        >
          {{ item }}
        </div>
      </div>
      <!--实际内容-->
      <div
        v-for="(item, i) in dataList"
        :key="'r-' + i"
        class="money-rules-table-row"
        :class="errorIndex === i ? 'row-is-error' : ''"
      >
        <div class="table-col col-t-0">
          第{{ item.level }}档
        </div>
        <div class="table-col col-t-1">
          <div
            v-if="isEdit && i !== (dataList.length - 1)"
            class="table-col-inner"
          >
            <InputNumber
              v-model="item.h5RangeMin"
              :min="0"
              :disabled="true"
            />
            <span style="margin: 0 5px">瓦至</span>
            <InputNumber
              v-model.trim="item.h5RangeMax"
              :min="0"
              @input="onInput"
            />
            <span style="margin-left: 5px">瓦之间</span>
          </div>
          <!--最后一个档位-->
          <div
            v-else-if="isEdit && i === (dataList.length - 1)"
            class="table-col-inner"
          >
            <InputNumber
              v-model="item.h5RangeMin"
              :disabled="true"
            />
            <span style="margin-left: 5px">瓦以上</span>
          </div>
          <div
            v-else
            class="table-col-inner"
          >
            {{ item.h5RangeMin }}瓦至{{ item.h5RangeMax || '∞' }}瓦之间
          </div>
        </div>
        <div
          class="table-col col-t-2"
        >
          <div
            v-if="isEdit"
            class="table-col-inner"
          >
            <InputNumber
              v-model="item.servicePrice"
              :min="0"
            />
            <span style="margin-left: 5px">元/小时</span>
          </div>
          <div
            v-else
            class="table-col-inner"
          >
            服务费{{ item.servicePrice }}元/小时
          </div>
        </div>
        <div
          v-if="isEdit"
          class="table-col col-t-3"
        >
          <yg-button
            type="text"
            disabled-text="此项暂时无法删除"
            :disabled="i === 0"
            @click="_clickDel(i)"
          >
            删除
          </yg-button>
        </div>
      </div>
      <!--最底部新增-->
      <div
        v-if="isEdit && dataList.length < 10"
        class="money-rules-table-row add-btn-row"
        @click.stop="_clickAdd"
      >
        <div class="table-col col-t-0">
          第{{ dataList.length + 1 }}档
        </div>
        <div class="table-col col-t-1">
          -
        </div>
        <div class="table-col col-t-2">
          -
        </div>
        <div class="table-col col-t-3">
          -
        </div>
        <div class="add-btn">
          - 增加第{{ dataList.length + 1 }}档位 -
        </div>
      </div>
    </div>

    <!-- 无配置时占位（与其他计费表单保持一致） -->
    <div
      v-else-if="!dataList.length"
      class="__g-no-data"
      style="height: 300px;"
    />

    <!-- ==================== 说明 ==================== -->
    <Alert
      v-if="isEdit"
      class="alert-gap"
    >
      说明
      <template slot="desc">
        <p>1、电费为固定单价，按实际充电度数计收，不区分时段；</p>
        <p>2、服务费四种收取方式互斥，只能选择一种；</p>
        <p>3、按功率阶梯收取时，按每半小时时段各自的功率落入的档位取单价，再乘以该时段时长累加；功率取值规则可选「每半小时取最大值」或「每半小时取平均值」；</p>
        <p>4、保底服务费为整单服务费下限，如按维度算出的服务费低于该值，则按保底金额收取；</p>
        <p>5、空载或6分钟内免费充电不收费，此时保底服务费不生效。</p>
      </template>
    </Alert>
  </div>
</template>

<script>
import { DevicePriceModel } from '@/pages/050__deviceManager/chargingPileList/model/DevicePriceModel';

export default {
  components: {},
  props: {
    isEdit: {
      type: Boolean,
      default: false
    },
    model: {
      type: Object,
      default: () => {
        return {};
      }
    },
    dataList: {
      type: Array,
      default: () => {
        return [];
      }
    }
  },
  data () {
    return {
      errorIndex: -1,
      feeTypeOptions: [
        { value: 1, label: '按时间', unit: '元/小时' },
        { value: 2, label: '按功率阶梯', unit: '元/小时' },
        { value: 3, label: '按使用电量', unit: '元/度' },
        { value: 4, label: '按次数', unit: '元/次' }
      ],
      powerValueTypeOptions: [
        { value: 2, label: '每半小时取最大值' },
        { value: 1, label: '每半小时取平均值' }
      ]
    };
  },
  computed: {
    tableHeaders () {
      return this.isEdit ? ['档位', '瓦数区间', '服务费单价', '操作'] : ['档位', '瓦数区间', '服务费单价'];
    },
    feeTypeLabel () {
      const item = this.feeTypeOptions.find(v => v.value === this.model.serviceFeeType);
      return item ? `${item.label}（${item.unit}）` : '-';
    },
    powerValueTypeLabel () {
      const item = this.powerValueTypeOptions.find(v => v.value === this.model.powerValueType);
      return item ? item.label : '-';
    },
    unitText () {
      const item = this.feeTypeOptions.find(v => v.value === this.model.serviceFeeType);
      return item ? item.unit : '';
    },
    h5MinServiceFee () {
      const fee = this.model.minServiceFee;
      if (!fee || Number(fee) <= 0) {
        return '不设保底服务费';
      }
      return `每次充电服务费不低于${fee}元`;
    }
  },
  methods: {
    onInput () {
      this.errorIndex = -1;
      this.resetRangeMin();
    },
    // 重新校验一遍(N+1档的min 需要等于 N档的max)
    resetRangeMin () {
      setTimeout(() => {
        this.dataList.forEach((v, i) => {
          v.level = i + 1;
          if (i !== 0) {
            v.h5RangeMin = this.dataList[i - 1].h5RangeMax;
          }
        });
        // 最后一档的max要置空
        const last = this.dataList[this.dataList.length - 1];
        if (last) {
          last.h5RangeMax = 0;
        }
      }, 50);
    },
    // 切换服务费方式时重置档位
    onFeeTypeChange () {
      this.errorIndex = -1;
      if (this.model.serviceFeeType === 2) {
        // 按功率阶梯：至少 2 档
        const list = [];
        for (let i = 0; i < 2; i++) {
          const item = new DevicePriceModel();
          item.level = i + 1;
          item.category = 4;
          item.h5RangeMin = i * 200;
          item.h5RangeMax = i === 1 ? 0 : 200;
          item.servicePrice = 0;
          list.push(item);
        }
        this.dataList.splice(0, this.dataList.length, ...list);
      } else {
        this.dataList.splice(0, this.dataList.length, ...DevicePriceModel.createInitListByDianNew());
      }
    },
    // 校验瓦数空值
    utilsPowerCheckEmpty () {
      const index = this.dataList.findIndex(v => (!v.h5RangeMin && v.h5RangeMin !== 0) || !v.h5RangeMax);
      if (index !== -1 && index !== (this.dataList.length - 1)) {
        this.$Message.warning(`「第${index + 1}档」的瓦数区间有误：值不能为空或0`);
        this.errorIndex = index;
        return false;
      }
      return true;
    },
    // 校验瓦数值
    utilsPowerCheckValue () {
      const index = this.dataList.findIndex(v => (v.h5RangeMin > v.h5RangeMax));
      if (index !== -1 && index !== (this.dataList.length - 1)) {
        this.$Message.warning(`「第${index + 1}档」的瓦数区间有误：值大小有误`);
        this.errorIndex = index;
        return false;
      }
      return true;
    },
    // 校验服务费单价
    utilsServicePriceCheck () {
      const index = this.dataList.findIndex(v => v.servicePrice === undefined || v.servicePrice === null || Number(v.servicePrice) < 0);
      if (index !== -1) {
        this.$Message.warning(`「第${index + 1}档」的服务费单价有误`);
        this.errorIndex = index;
        return false;
      }
      return true;
    },
    _clickDel (i) {
      if (this.dataList.length <= 2) {
        return this.$Message.info('至少要保留2个档位');
      }
      this.dataList.splice(i, 1);
      this.resetRangeMin();
    },
    _clickAdd () {
      if (this.utilsPowerCheckEmpty() && this.utilsPowerCheckValue() && this.utilsServicePriceCheck()) {
        const last = this.dataList[this.dataList.length - 1];
        const item = new DevicePriceModel();
        item.level = this.dataList.length + 1;
        item.category = 4;
        item.h5RangeMin = last.h5RangeMax;
        item.h5RangeMax = 0;
        item.servicePrice = 0;
        this.dataList.push(item);
        this.resetRangeMin();
      }
    },
    // 外部调用：提交前校验
    $checkSubmit () {
      // 电费固定单价
      if (this.model.chargePrice === undefined || this.model.chargePrice === null || Number(this.model.chargePrice) <= 0) {
        return { success: false, msg: this.$Message.warning('请设置电费单价，且大于0') };
      }
      // 服务费收取方式
      if (!this.model.serviceFeeType) {
        return { success: false, msg: this.$Message.warning('请选择服务费收取方式') };
      }
      // 服务费单价
      if (this.model.serviceFeeType === 2) {
        // 按功率阶梯需选择功率取值规则
        if (this.model.powerValueType !== 1 && this.model.powerValueType !== 2) {
          return { success: false, msg: this.$Message.warning('请选择功率取值规则') };
        }
        if (!this.utilsPowerCheckEmpty() || !this.utilsPowerCheckValue() || !this.utilsServicePriceCheck()) {
          return { success: false };
        }
      } else {
        const item = this.dataList[0];
        if (!item || item.servicePrice === undefined || item.servicePrice === null || Number(item.servicePrice) < 0) {
          return { success: false, msg: this.$Message.warning('请设置服务费单价') };
        }
      }
      // 保底服务费（可为0）
      if (this.model.minServiceFee === undefined || this.model.minServiceFee === null || this.model.minServiceFee === '' || Number(this.model.minServiceFee) < 0) {
        return { success: false, msg: this.$Message.warning('请设置保底服务费（可为0）') };
      }
      this.errorIndex = -1;
      return { success: true };
    }
  }
};
</script>
